import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';

/**
 * DEDUPE-REQUEST INTERCEPTOR
 * ──────────────────────────
 * Last-line defence against double-click duplicate writes.
 *
 * Cashier double-clicks "Register Patient" / "Proceed to Payment" /
 * "Confirm". Without this guard, two POSTs hit the API back-to-back, both
 * succeed, and a duplicate row is created.
 *
 * Strategy: for mutating verbs (POST / PUT / PATCH / DELETE) we hash
 * `method + url + body` and remember it for IN_FLIGHT_WINDOW_MS. A second
 * request with the same hash arriving inside the window is SILENTLY DROPPED
 * (the dedupe interceptor is outermost, so it completes the stream with EMPTY)
 * — no actual write reaches the server and components NEVER see a spurious
 * error. Earlier this threw a synthetic 409; but because this interceptor sits
 * outside ErrorInterceptor, that 409 bypassed the intended swallow and reached
 * component `.subscribe({ error })` handlers as a FALSE "409 conflict" (e.g.
 * billing-counter showed "another user updated this payment first" on a mere
 * double-click). Swallowing at source here is the correct single point.
 *
 * NOTE: offline-queue REPLAYS (NetworkGuardService.retryAll) send the
 * `X-Allow-Duplicate` header to bypass this guard entirely — replay de-dup is
 * the server-side Idempotency-Key's job, and skipping here keeps the EMPTY
 * completion from colliding with retryAll's `undefined`-means-swallowed-401
 * detection.
 *
 * GET / HEAD / OPTIONS are never deduped (idempotent by HTTP semantics).
 * Auth/notification endpoints are also skipped (re-issuing is legitimate).
 *
 * Pairs with the server-side `Idempotency-Key` mechanism. This catches the
 * common case (rapid clicks) at zero round-trip cost; the server header
 * mechanism handles network retries and replays.
 */
@Injectable()
export class DedupeRequestInterceptor implements HttpInterceptor {
  private static readonly inFlight = new Map<string, number>();

  /** 1.2s covers the typical user double-click reflex. Long enough that two
   *  intentional separate clicks are NOT blocked. */
  private static readonly IN_FLIGHT_WINDOW_MS = 1200;

  private static readonly SKIP_PATTERNS = [
    /\/api\/auth\/login\b/i,
    /\/api\/auth\/refresh\b/i,
    /\/api\/auth\/logout\b/i,
    /\/api\/notifications\b/i,
  ];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldSkip(req)) return next.handle(req);

    const now = Date.now();
    this.purgeExpired(now);

    const key = this.buildKey(req);
    const startedAt = DedupeRequestInterceptor.inFlight.get(key);

    if (startedAt != null && now - startedAt < DedupeRequestInterceptor.IN_FLIGHT_WINDOW_MS) {
      console.warn(
        `[dedupe] Dropped duplicate ${req.method} ` +
        `(prior fired ${now - startedAt}ms ago)`);
      // AUDIT FIX (2026-06-16): silently swallow the duplicate (complete with EMPTY) instead of
      // throwing. Earlier history: returning status 0 collided with offline-failure detection
      // (May 2026), so it was switched to a synthetic 409 — but 409 collided with REAL server
      // "409 conflict" handling in components (false "another user updated this" alerts on a
      // double-click). Since this interceptor is outermost, the only correct place to suppress a
      // dedupe-block from ALL components is right here: EMPTY → the first click's write is the
      // single source of truth, the duplicate just no-ops. (Log the method only, not the full URL,
      // to avoid persisting patient/visit IDs in a shared-PC browser console.)
      return EMPTY;
    }

    DedupeRequestInterceptor.inFlight.set(key, now);

    return new Observable<HttpEvent<unknown>>(observer => {
      const sub = next.handle(req).subscribe({
        next: ev => observer.next(ev),
        error: err => {
          // Clear lock on error so the user can legitimately retry.
          DedupeRequestInterceptor.inFlight.delete(key);
          observer.error(err);
        },
        complete: () => {
          // Brief tail-window so rapid retries within reflex are still blocked.
          setTimeout(() => DedupeRequestInterceptor.inFlight.delete(key), 250);
          observer.complete();
        }
      });
      return () => sub.unsubscribe();
    });
  }

  private shouldSkip(req: HttpRequest<unknown>): boolean {
    if (req.method === 'GET' || req.method === 'OPTIONS' || req.method === 'HEAD') return true;
    if (req.headers.has('X-Allow-Duplicate')) return true;
    return DedupeRequestInterceptor.SKIP_PATTERNS.some(p => p.test(req.url));
  }

  private hashBody(body: unknown): string {
    if (body == null) return '';
    try {
      const s = typeof body === 'string' ? body : JSON.stringify(body);
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h) + s.charCodeAt(i);
        h |= 0;
      }
      return h.toString(36);
    } catch {
      return '';
    }
  }

  private buildKey(req: HttpRequest<unknown>): string {
    return `${req.method}::${req.url}::${this.hashBody(req.body)}`;
  }

  private purgeExpired(now: number): void {
    for (const [k, v] of DedupeRequestInterceptor.inFlight) {
      if (now - v > DedupeRequestInterceptor.IN_FLIGHT_WINDOW_MS) {
        DedupeRequestInterceptor.inFlight.delete(k);
      }
    }
  }
}
